import React, { useEffect, useState } from "react";
import {
  TextInput,
  Button,
  Select,
  Flex,
  Divider,
  Text,
  Drawer,
  Box,
} from "@mantine/core";

import "dayjs/locale/uk";
import "dayjs/locale/en";

import useDevelopmentStore from "../../../store/useDevelopmentStore";
import useAuthStore from "../../../store/useAuthStore";
import useDashboardStore from "../../../store/useDashboardStore";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "@mantine/hooks";
import dayjs from "dayjs";

import useProfileStore from "../../../store/useProfileStore";
import ModalWithSearchBar from "../../search-bar/GoalSearchBar";
import { InfoTooltip } from "../../../shared/components/InfoTooltip";
import { Goal } from "../../../types/api";
import { DateInput, DatesProvider } from "@mantine/dates";
import { IconCalendar } from "../../../shared/svg/IconCalendar";

type ResourceLink = { learning_course_id: number };

interface GoalFormValues {
  title: string;
  description: string;
  status: "planned" | "in_progress" | "completed";
  priority: "low" | "medium" | "high";
  resource_links?: ResourceLink[] | [];
  deadline: string;
}

interface GoalModalProps {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  setIsEditOpen: (value: boolean) => void;
  setEditGoal: (goal: Goal) => void;
}

const statusMapping: Record<"planned" | "in_progress" | "completed", string> = {
  planned: "Planned",
  in_progress: "In Progress",
  completed: "Completed",
};

const priorityMapping: Record<"low" | "medium" | "high", string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
};

export const GoalModal = ({
  isOpen,
  setIsOpen,
  setIsEditOpen,
  setEditGoal,
}: GoalModalProps) => {
  const [formValues, setFormValues] = useState<GoalFormValues>({
    title: "",
    description: "",
    deadline: "",
    status: "planned",
    priority: "high",
    resource_links: [],
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const matches = useMediaQuery("(max-width: 650px)");
  const { addGoalToCalendar, currentPlanId } = useDevelopmentStore();
  const { fetchDashboard } = useDashboardStore();
  const { access_token } = useAuthStore();
  const { profileLanguage } = useProfileStore();

  const { t } = useTranslation();

  useEffect(() => {
    dayjs.locale(Object.keys(profileLanguage!)[0]);
  }, [profileLanguage]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: string
  ) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleStatusChange = (value: string | null) => {
    if (value && ["planned", "in_progress", "completed"].includes(value)) {
      setFormValues((prev) => ({
        ...prev,
        status: value as "planned" | "in_progress" | "completed",
      }));
    }
  };

  const handleDateChange = (value: string) => {
    setFormValues((prev) => ({
      ...prev,
      deadline: value,
    }));
  };

  const handlePriorityChange = (value: string | null) => {
    if (value && ["low", "medium", "high"].includes(value)) {
      setFormValues((prev) => ({
        ...prev,
        priority: value as "low" | "medium" | "high",
      }));
    }
  };

  const handleResourceLink = (links: ResourceLink[]) => {
    setFormValues((prev) => ({
      ...prev,
      resource_links: links,
    }));
  };

  const handleSubmit = async () => {
    let validationErrors: { [key: string]: string } = {};
    if (!formValues.title) {
      validationErrors.title = t("form.titleRequired");
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    try {
      const res = await addGoalToCalendar({
        planId: currentPlanId!,
        credentials: { ...formValues },
      });
      fetchDashboard(access_token!);
      resetForm();
      setEditGoal(res.data);
      setIsEditOpen(true);
      setIsOpen(false);
    } catch (err: any) {
      setErrors(
        Object.keys(err.response.data.errors).reduce((acc, key) => {
          acc[key] = err.response.data.errors[key][0];
          return acc;
        }, {} as { [key: string]: string })
      );
    }
  };

  const resetForm = () => {
    setFormValues({
      title: "",
      description: "",
      status: "planned",
      priority: "high",
      resource_links: [],
      deadline: "",
    });
    setErrors({});
  };

  const focusNextInput = (currentName: string) => {
    const fieldOrder = ["title", "description"];
    const currentIndex = fieldOrder.indexOf(currentName);
    const nextField = fieldOrder[currentIndex + 1];
    if (nextField) {
      const next = document.querySelector(
        `input[name="${nextField}"]`
      ) as HTMLElement | null;
      next?.focus();
    }
  };

  return (
    <>
      <Drawer
        zIndex={301}
        opened={isOpen}
        onClose={() => {
          resetForm();
          setIsOpen(false);
        }}
        title={<Text fw={600}>{t("modal.addNewGoal")}</Text>}
        size="lg"
        padding={30}
        transitionProps={{ duration: 300 }}
      >
        <TextInput
          name="title"
          mt={5}
          data-autofocus
          label={t("form.title")}
          placeholder={t("form.enterGoalTitle")}
          value={formValues.title}
          onChange={(e) => handleChange(e, "title")}
          required
          error={errors.title}
          rightSection={
            <div style={{ display: "flex", height: "100%" }}>
              <InfoTooltip label={t("goal_title_description")} size={25} />
            </div>
          }
          onKeyDown={async (e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              focusNextInput("title");
            }
          }}
        />

        <TextInput
          name="description"
          label={t("form.description")}
          placeholder={t("form.enterGoalDescription")}
          value={formValues.description}
          onChange={(e) => handleChange(e, "description")}
          mt="xl"
          error={errors.description}
          rightSection={
            <div style={{ display: "flex", height: "100%" }}>
              <InfoTooltip label={t("goal_notes_description")} size={25} />
            </div>
          }
        />
        <DatesProvider settings={{ locale: Object.keys(profileLanguage!)[0] }}>
          <DateInput
            mt={32}
            label={t("deadline")}
            placeholder={t("choose_date")}
            value={formValues.deadline ? new Date(formValues.deadline) : null}
            onChange={(date) =>
              handleDateChange(date ? dayjs(date).format("YYYY-MM-DD") : "")
            }
            clearable
            popoverProps={{
              zIndex: 302,
            }}
            leftSection={<IconCalendar />}
            renderDay={(date) => {
              const isToday = dayjs(date).isSame(new Date(), "day");

              return (
                <Box
                  style={{
                    backgroundColor: isToday ? "#faf0fd" : "transparent",
                    color: isToday ? "#862e9c" : "inherit",
                    border: isToday ? "1px solid #862e9c" : "none",
                    borderRadius: 6,
                    width: 34,
                    height: 34,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: isToday ? 700 : 400,
                  }}
                >
                  {date.getDate()}
                </Box>
              );
            }}
          />
        </DatesProvider>

        <Flex direction={matches ? "column" : "row"} gap="md" mt="lg">
          <Select
            label={t("form.status")}
            value={formValues.status}
            onChange={handleStatusChange}
            data={Object.keys(statusMapping).map((key) => ({
              value: key,
              label: t(`status.${key}`),
            }))}
            w={matches ? "100%" : "50%"}
            comboboxProps={{
              zIndex: 302,
              withinPortal: true,
            }}
          />

          <Select
            label={t("form.priority")}
            value={formValues.priority}
            onChange={handlePriorityChange}
            data={Object.keys(priorityMapping).map((key) => ({
              value: key,
              label: t(`priority.${key}`),
            }))}
            w={matches ? "100%" : "50%"}
            comboboxProps={{
              zIndex: 302,
              withinPortal: true,
            }}
          />
        </Flex>
        <Divider my="xl" color="gray.4" />

        <ModalWithSearchBar handleResourceLink={handleResourceLink} />

        <Divider my="xl" color="gray.4" />

        <Flex align="center" justify="space-between" mt={30}>
          <Button
            onClick={() => {
              resetForm();
              setIsOpen(false);
            }}
            size={matches ? "compact-md" : "md"}
            variant="outline"
          >
            {t("cancel")}
          </Button>

          <Button size={matches ? "compact-md" : "md"} onClick={handleSubmit}>
            {t("form.submit")}
          </Button>
        </Flex>
      </Drawer>
    </>
  );
};

export default GoalModal;
